import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/about#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/about`,
      "name": "About DVS Power Washing | Family Minority Owned Dallas TX",
      "description": "Learn about DVS Power Washing — a family minority owned pressure washing company in Dallas Metroplex, TX. Built on integrity, precision, and 100% customer satisfaction.",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "about": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "About", "item": `${import.meta.env.VITE_SITE_URL}/about` }
        ]
      }
    },
    {
      "@type": "Organization",
      "@id": `${import.meta.env.VITE_SITE_URL}/#business`,
      "name": "DVS Power Washing",
      "url": import.meta.env.VITE_SITE_URL,
      "telephone": "+12146368343",
      "description": "Family minority owned pressure washing company in Dallas Metroplex, TX. Committed to quality, integrity, and 100% customer satisfaction.",
      "foundingLocation": {
        "@type": "Place",
        "name": "Dallas, Texas, USA"
      },
      "sameAs": [
        "https://www.instagram.com/dvs_power_washing_dallas/"
      ]
    }
  ]
};

const AboutPage = () => {
  useEffect(() => {
    // Canonical tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://dvspowerwashingdallas.com/about';

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.text = JSON.stringify(ABOUT_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-about');
      if (el) el.remove();
    };
  }, []);

  const values = [
    {
      icon: 'ri-shield-star-fill',
      title: 'Integrity',
      description: 'We do what we say and stand behind our work with complete honesty.',
    },
    {
      icon: 'ri-focus-3-fill',
      title: 'Precision',
      description: 'Exceptional attention to detail in every job we complete.',
    },
    {
      icon: 'ri-customer-service-2-fill',
      title: 'Service',
      description: 'Your satisfaction is our mission. We go above and beyond.',
    },
    {
      icon: 'ri-medal-fill',
      title: 'Excellence',
      description: 'We strive for perfection in every aspect of our work.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E3A5D] to-[#1A5280]"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Built on Dedication.<br />Driven by Quality.
          </h1>
          <p className="text-xl lg:text-2xl text-[#4FB3D9]">
            Family minority owned and operated in Dallas Metroplex, Texas
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-bold text-[#0E3A5D] uppercase tracking-wider mb-6">Our Story</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-8">
            From Family Values to Excellence
          </h2>

          <div className="space-y-8 text-lg text-[#5A6C7D] leading-relaxed">
            <p>
              DVS Power Washing was founded on strong family values and a commitment to excellence. As a family minority owned company based in the Dallas Metroplex, Texas, we bring dedication, precision, and attention to detail to every pressure washing job we undertake.
            </p>

            <p>
              We understand that your property is one of your most valuable investments. That's why we approach every project with the same care and professionalism we'd want for our own homes. No shortcuts. No compromises. Just honest, quality work that speaks for itself.
            </p>

            <p className="text-2xl italic text-[#4FB3D9] text-center py-12 font-medium">
              "We don't cut corners — we clean them."
            </p>

            <p>
              Our team takes pride in transforming properties throughout the Dallas Metroplex and the surrounding areas. Whether it's restoring a driveway, soft washing a roof, or cleaning a commercial property, we treat every job with the same level of importance and dedication.
            </p>

            <p>
              What sets us apart is our customer-first mindset. We're not satisfied until you're completely happy with the results. Our 100% satisfaction guarantee isn't just a promise—it's a reflection of our commitment to earning your trust and exceeding your expectations.
            </p>

            <p>
              When you choose DVS Power Washing, you're choosing a company that values integrity, reliability, and quality above all else. We're here to serve our community with honor and dedication.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] text-center mb-16">
            Our Core Values
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5">
                  <i className={`${value.icon} text-6xl text-[#0E3A5D]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{value.title}</h3>
                <p className="text-base text-[#5A6C7D] leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0E3A5D]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
              <i className="ri-team-fill text-8xl text-[#4FB3D9]"></i>
            </div>

            <div className="text-center lg:text-left">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Proud Family Minority Owned Business
              </h2>
              <p className="text-lg text-[#4FB3D9] mb-5">
                Supporting our community with quality service
              </p>
              <p className="text-base text-white/90">
                As a family minority owned business, we're committed to providing exceptional service to our Dallas Metroplex community. We take pride in our work and in building lasting relationships with our customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-xl text-[#5A6C7D] mb-10">
            Let us show you what quality service really means
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
            <a
              href="tel:2146368343"
              className="bg-[#4FB3D9] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#3A9DC4] transition-all whitespace-nowrap flex items-center space-x-3 w-full sm:w-auto justify-center"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span>Call (214) 636-8343</span>
            </a>
            <Link
              to="/contact"
              className="bg-[#0E3A5D] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#0A2D47] transition-all whitespace-nowrap w-full sm:w-auto text-center"
            >
              Get Free Quote
            </Link>
          </div>

          <div className="flex items-center justify-center">
            <a
              href="https://www.instagram.com/dvs_power_washing_dallas/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 bg-gradient-to-r from-[#f09433] via-[#e6683c] via-[#dc2743] via-[#cc2366] to-[#bc1888] text-white px-8 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-instagram-fill text-2xl"></i>
              </div>
              <span>Follow Us on Instagram</span>
              <span className="text-white/80 text-base font-normal">@dvs_power_washing_dallas</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
